export type WsPerfEntry = {
  eventType: string;
  payloadChars: number;
  parseMs: number;
  dispatchMs: number;
  frameMs?: number;
};

type PendingPaint = {
  entry: WsPerfEntry;
  startedAt: number;
};

declare global {
  interface Window {
    __wsPerfEntries?: WsPerfEntry[];
  }
}

const MAX_ENTRIES = 100;
const SLOW_TASK_MS = 50;
let pendingPaint: PendingPaint | null = null;
let paintScheduled = false;

const round = (value: number) => Math.round(value * 100) / 100;

const isEnabled = () => process.env.NODE_ENV === 'development';

const logSlowEntry = (entry: WsPerfEntry) => {
  if ((entry.parseMs + entry.dispatchMs) >= SLOW_TASK_MS) {
    console.warn('[ws-perf]', JSON.stringify(entry));
  }
};

const schedulePaintMeasurement = () => {
  if (paintScheduled) {
    return;
  }

  paintScheduled = true;
  window.requestAnimationFrame(() => {
    paintScheduled = false;
    if (!pendingPaint) {
      return;
    }

    const { entry, startedAt } = pendingPaint;
    pendingPaint = null;
    entry.frameMs = round(performance.now() - startedAt);
    if (entry.frameMs >= SLOW_TASK_MS) {
      console.warn('[ws-perf]', JSON.stringify(entry));
    }
  });
};

export const startWsPerfProbe = (payload: string) => {
  if (!isEnabled()) {
    return undefined;
  }

  const startedAt = performance.now();

  return {
    complete(eventType: unknown, parseMs: number, dispatchMs: number) {
      const entry: WsPerfEntry = {
        eventType: String(eventType).slice(0, 128),
        payloadChars: payload.length,
        parseMs: round(parseMs),
        dispatchMs: round(dispatchMs),
      };
      const entries = window.__wsPerfEntries || [];

      entries.push(entry);
      if (entries.length > MAX_ENTRIES) {
        entries.splice(0, entries.length - MAX_ENTRIES);
      }
      window.__wsPerfEntries = entries;

      const finishedAt = performance.now();
      if ((entry.parseMs + entry.dispatchMs) >= SLOW_TASK_MS) {
        performance.measure(`ws:${entry.eventType}`, {
          start: startedAt,
          end: finishedAt,
        });
      }

      pendingPaint = { entry, startedAt };
      schedulePaintMeasurement();
      logSlowEntry(entry);
    },
  };
};
